import { useQuery } from "@apollo/client";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Title, useTranslate } from "react-admin";
import { useNavigate } from "react-router-dom";

import { RequirePermission } from "../components/RequirePermission";
import {
  SalesforceColumnChart,
  type SalesforceColumnDatum,
  SalesforceDashboardCard,
  SalesforceDonutChart,
  SalesforceStackedColumnChart,
} from "../components/SalesforceDashboardCharts";
import { ResourceName, SALESFORCE_RECORDS_ROUTE } from "../constants/resources";
import {
  CustomerServiceSalesforceDashboardDocument,
  CustomerServiceSalesforceDashboardQuery,
  SalesforcePeriodGranularity,
  SalesforceRecordsKind,
  SalesforceSegment,
} from "../generated/graphql";
import {
  BLANK_CATEGORY_LABEL,
  buildCancellationTypeSeries,
  buildDonutData,
  buildStatusSeries,
  cancellationTypeLabelKey,
  type DonutSlice,
  drillDownSearch,
  formatCount,
  formatPeriodLong,
  type PeriodGranularity,
  SALESFORCE_CHART_COLORS,
  type SalesforceDrillDown,
  salesforceSyncedAtFormat,
  sliceDisplayLabel,
  toNumberOrNull,
} from "../lib/salesforceDashboard";
import { color, space } from "../theme/designTokens";

type Dashboard = CustomerServiceSalesforceDashboardQuery["customerServiceSalesforceDashboard"];
type ContractSegment = Dashboard["contractSegments"][number];
type StackedComponentKey = "createdMonthly" | "createdWeekly" | "createdDaily" | "cancelledMonthly" | "endedMonthly";
type XAxisTitleKey = "createdAt" | "cancelledAt" | "endedAt";

const colors = SALESFORCE_CHART_COLORS;
const ICON_TILE_SIZE_PX = 32;
const WARNING_ICON_SIZE_PX = 16;
const LICENCE_DONUT_OPTIONS = { maxGroups: 6, combineSmallGroups: true };

const columnSx = { display: "flex", flexDirection: "column", gap: `${space.md}px`, minWidth: 0 } as const;

type DrillDownHandler = (drillDown: SalesforceDrillDown) => void;

const GRANULARITY: Record<PeriodGranularity, SalesforcePeriodGranularity> = {
  day: SalesforcePeriodGranularity.Day,
  week: SalesforcePeriodGranularity.Week,
  month: SalesforcePeriodGranularity.Month,
};

const HISTORY_KIND: Record<StackedComponentKey, SalesforceRecordsKind> = {
  createdMonthly: SalesforceRecordsKind.Created,
  createdWeekly: SalesforceRecordsKind.Created,
  createdDaily: SalesforceRecordsKind.Created,
  cancelledMonthly: SalesforceRecordsKind.Cancelled,
  endedMonthly: SalesforceRecordsKind.Ended,
};

const statusLabel = (value: string | null): string => value ?? BLANK_CATEGORY_LABEL;

const findSegment = (segments: ContractSegment[], recordType: SalesforceSegment): ContractSegment | undefined =>
  segments.find(segment => segment.recordType === recordType);

type StackedCardProps = {
  componentKey: StackedComponentKey;
  segment: SalesforceSegment;
  asOf: string;
  granularity: PeriodGranularity;
  xAxisTitleKey: XAxisTitleKey;
  kind: "status" | "cancellationType";
  history: Dashboard["history"][StackedComponentKey];
  onDrillDown: DrillDownHandler;
};

const StackedCard = ({
  componentKey,
  segment,
  asOf,
  granularity,
  xAxisTitleKey,
  kind,
  history,
  onDrillDown,
}: StackedCardProps) => {
  const translate = useTranslate();

  const series = useMemo(
    () => (kind === "status" ? buildStatusSeries(history, segment) : buildCancellationTypeSeries(history, segment)),
    [kind, history, segment]
  );
  const cancellationLabel = useCallback(
    (value: string | null): string => {
      if (value === null) return BLANK_CATEGORY_LABEL;
      const key = cancellationTypeLabelKey(value);
      return key ? translate(`salesforceDashboard.cancellationTypes.${key}`) : value;
    },
    [translate]
  );

  const title = translate(`salesforceDashboard.components.${componentKey}.title`, { segment });
  const categoryLabel = kind === "status" ? statusLabel : cancellationLabel;
  const handleSegmentClick = useCallback(
    (periodStart: string, category: string | null) =>
      onDrillDown({
        kind: "records",
        title: `${title} · ${formatPeriodLong(periodStart, granularity)} · ${categoryLabel(category)}`,
        input: {
          kind: HISTORY_KIND[componentKey],
          segment,
          periodStart,
          granularity: GRANULARITY[granularity],
          labels: [category],
        },
      }),
    [title, granularity, categoryLabel, componentKey, segment, onDrillDown]
  );

  const handlePeriodClick = useCallback(
    (periodStart: string) =>
      onDrillDown({
        kind: "records",
        title: `${title} · ${formatPeriodLong(periodStart, granularity)}`,
        input: { kind: HISTORY_KIND[componentKey], segment, periodStart, granularity: GRANULARITY[granularity] },
      }),
    [title, granularity, componentKey, segment, onDrillDown]
  );

  return (
    <SalesforceDashboardCard
      asOf={asOf}
      definition={translate(`salesforceDashboard.components.${componentKey}.definition`)}
      title={title}
    >
      <SalesforceStackedColumnChart
        categoryLabel={categoryLabel}
        onPeriodClick={handlePeriodClick}
        onSegmentClick={handleSegmentClick}
        granularity={granularity}
        legendTitle={translate(
          kind === "status" ? "salesforceDashboard.axes.status" : "salesforceDashboard.axes.cancellationType"
        )}
        series={series}
        xAxisTitle={translate(`salesforceDashboard.axes.${xAxisTitleKey}`)}
      />
    </SalesforceDashboardCard>
  );
};

type SegmentColumnProps = {
  segment: SalesforceSegment;
  products: Dashboard["activeB2BProducts"];
  history: Dashboard["history"];
  asOf: string;
  onDrillDown: DrillDownHandler;
  children?: ReactNode;
};

const SegmentColumn = ({ segment, products, history, asOf, onDrillDown, children }: SegmentColumnProps) => {
  const translate = useTranslate();
  const donut = useMemo(() => buildDonutData(products), [products]);
  const title = translate("salesforceDashboard.components.activeSubscriptions.title", { segment });
  const handleSliceClick = useCallback(
    (slice: DonutSlice) =>
      onDrillDown({
        kind: "records",
        title: `${title} · ${sliceDisplayLabel(slice, translate("salesforceDashboard.other"))}`,
        input: { kind: SalesforceRecordsKind.ActiveProducts, segment, labels: slice.labels },
      }),
    [title, segment, translate, onDrillDown]
  );

  return (
    <Box sx={columnSx}>
      <SalesforceDashboardCard
        asOf={asOf}
        definition={translate(
          segment === SalesforceSegment.B2C
            ? "salesforceDashboard.components.activeSubscriptions.b2cDefinition"
            : "salesforceDashboard.components.activeSubscriptions.definition",
          { segment }
        )}
        title={title}
      >
        <SalesforceDonutChart
          data={donut}
          legendTitle={translate("salesforceDashboard.axes.productName")}
          onSliceClick={handleSliceClick}
        />
      </SalesforceDashboardCard>
      {children}
      <StackedCard
        asOf={asOf}
        componentKey="createdMonthly"
        granularity="month"
        history={history.createdMonthly}
        kind="status"
        onDrillDown={onDrillDown}
        segment={segment}
        xAxisTitleKey="createdAt"
      />
      <StackedCard
        asOf={asOf}
        componentKey="createdWeekly"
        granularity="week"
        history={history.createdWeekly}
        kind="status"
        onDrillDown={onDrillDown}
        segment={segment}
        xAxisTitleKey="createdAt"
      />
      <StackedCard
        asOf={asOf}
        componentKey="createdDaily"
        granularity="day"
        history={history.createdDaily}
        kind="status"
        onDrillDown={onDrillDown}
        segment={segment}
        xAxisTitleKey="createdAt"
      />
      <StackedCard
        asOf={asOf}
        componentKey="cancelledMonthly"
        granularity="month"
        history={history.cancelledMonthly}
        kind="cancellationType"
        onDrillDown={onDrillDown}
        segment={segment}
        xAxisTitleKey="cancelledAt"
      />
      <StackedCard
        asOf={asOf}
        componentKey="endedMonthly"
        granularity="month"
        history={history.endedMonthly}
        kind="cancellationType"
        onDrillDown={onDrillDown}
        segment={segment}
        xAxisTitleKey="endedAt"
      />
    </Box>
  );
};

type ContractMetricKey = "totalMrr" | "averageMrr" | "averageActiveSubscriptionItems";

type ContractMetricCardProps = {
  metric: ContractMetricKey;
  dashboard: Dashboard;
  asOf: string;
  onDrillDown: DrillDownHandler;
};

const ContractMetricCard = ({ metric, dashboard, asOf, onDrillDown }: ContractMetricCardProps) => {
  const translate = useTranslate();
  const { contractSegments, currency } = dashboard;

  const data = useMemo<SalesforceColumnDatum[]>(
    () =>
      [SalesforceSegment.B2B, SalesforceSegment.B2C].map(recordType => ({
        label: recordType,
        value: toNumberOrNull(findSegment(contractSegments, recordType)?.[metric]),
      })),
    [contractSegments, metric]
  );
  const eligible = (recordType: SalesforceSegment): string =>
    formatCount(findSegment(contractSegments, recordType)?.eligibleContracts ?? 0);
  const yAxisTitle =
    metric === "averageActiveSubscriptionItems"
      ? translate("salesforceDashboard.axes.averageActiveSubscriptionItems")
      : translate(`salesforceDashboard.axes.${metric}`, { currency });

  const title = translate(`salesforceDashboard.components.${metric}`);
  const handleBarClick = useCallback(
    (datum: SalesforceColumnDatum) =>
      onDrillDown({
        kind: "contracts",
        title: `${title} · ${datum.label}`,
        segment: datum.label as SalesforceSegment,
      }),
    [title, onDrillDown]
  );

  return (
    <SalesforceDashboardCard
      asOf={asOf}
      definition={translate("salesforceDashboard.components.contractBasis", {
        b2b: eligible(SalesforceSegment.B2B),
        b2c: eligible(SalesforceSegment.B2C),
      })}
      title={title}
    >
      <SalesforceColumnChart
        data={data}
        onBarClick={handleBarClick}
        xAxisTitle={translate("salesforceDashboard.axes.recordType")}
        yAxisTitle={yAxisTitle}
      />
    </SalesforceDashboardCard>
  );
};

const DashboardGrid = ({
  dashboard,
  asOf,
  onDrillDown,
}: {
  dashboard: Dashboard;
  asOf: string;
  onDrillDown: DrillDownHandler;
}) => {
  const translate = useTranslate();
  const licenceDonut = useMemo(
    () => buildDonutData(dashboard.activeLicenceProducts, LICENCE_DONUT_OPTIONS),
    [dashboard.activeLicenceProducts]
  );
  const licenceTitle = translate("salesforceDashboard.components.activeLicences.title");
  const handleLicenceClick = useCallback(
    (slice: DonutSlice) =>
      onDrillDown({
        kind: "records",
        title: `${licenceTitle} · ${sliceDisplayLabel(slice, translate("salesforceDashboard.other"))}`,
        input: { kind: SalesforceRecordsKind.ActiveLicences, labels: slice.labels },
      }),
    [licenceTitle, translate, onDrillDown]
  );

  return (
    <Box
      sx={{
        display: "grid",
        gap: `${space.md}px`,
        gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
      }}
    >
      <SegmentColumn
        asOf={asOf}
        history={dashboard.history}
        onDrillDown={onDrillDown}
        products={dashboard.activeB2CProducts}
        segment={SalesforceSegment.B2C}
      >
        <ContractMetricCard asOf={asOf} dashboard={dashboard} onDrillDown={onDrillDown} metric="totalMrr" />
      </SegmentColumn>
      <SegmentColumn
        asOf={asOf}
        history={dashboard.history}
        onDrillDown={onDrillDown}
        products={dashboard.activeB2BProducts}
        segment={SalesforceSegment.B2B}
      />
      <Box sx={columnSx}>
        <SalesforceDashboardCard
          asOf={asOf}
          definition={translate("salesforceDashboard.components.activeLicences.definition", {
            count: LICENCE_DONUT_OPTIONS.maxGroups,
          })}
          title={licenceTitle}
        >
          <SalesforceDonutChart
            data={licenceDonut}
            legendTitle={translate("salesforceDashboard.axes.productName")}
            onSliceClick={handleLicenceClick}
          />
        </SalesforceDashboardCard>
        <ContractMetricCard asOf={asOf} dashboard={dashboard} onDrillDown={onDrillDown} metric="averageMrr" />
        <ContractMetricCard
          asOf={asOf}
          dashboard={dashboard}
          onDrillDown={onDrillDown}
          metric="averageActiveSubscriptionItems"
        />
      </Box>
    </Box>
  );
};

const HeaderNotice = ({ children }: { children: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: `${space.sm}px`, fontSize: "12px", fontWeight: 700 }}>
    <WarningAmberOutlinedIcon
      aria-hidden
      sx={{
        flex: "none",
        width: `${WARNING_ICON_SIZE_PX}px`,
        height: `${WARNING_ICON_SIZE_PX}px`,
        color: colors.warning,
      }}
    />
    {children}
  </Box>
);

type NoticeKey =
  | "notices.unclassifiedSubscriptions"
  | "notices.missingSubscriptionCountContracts"
  | "notices.missingMrrContracts"
  | "history.missingDates.created"
  | "history.missingDates.cancellation"
  | "history.missingDates.end";

type QualityNotice = { key: NoticeKey; count: number; recordType?: SalesforceSegment };

const qualityNotices = (dashboard: Dashboard): QualityNotice[] => {
  const { contractSegments, history } = dashboard;
  const candidates: QualityNotice[] = [
    { key: "notices.unclassifiedSubscriptions", count: dashboard.unclassifiedSubscriptions },
    ...contractSegments.map(
      (segment): QualityNotice => ({
        key: "notices.missingSubscriptionCountContracts",
        count: segment.missingSubscriptionCountContracts,
        recordType: segment.recordType,
      })
    ),
    ...contractSegments.map(
      (segment): QualityNotice => ({
        key: "notices.missingMrrContracts",
        count: segment.missingMrrContracts,
        recordType: segment.recordType,
      })
    ),
    { key: "history.missingDates.created", count: history.missingCreatedDates },
    { key: "history.missingDates.cancellation", count: history.missingCancellationDates },
    { key: "history.missingDates.end", count: history.missingEndDates },
  ];
  return candidates.filter(candidate => candidate.count > 0);
};

const SalesforceDashboardContent = () => {
  const translate = useTranslate();
  const { data, loading, error, refetch } = useQuery<CustomerServiceSalesforceDashboardQuery>(
    CustomerServiceSalesforceDashboardDocument,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    }
  );
  const dashboard = data?.customerServiceSalesforceDashboard;
  const syncedAt = dashboard?.lastSyncedAt ? salesforceSyncedAtFormat.format(new Date(dashboard.lastSyncedAt)) : null;
  const asOf = syncedAt === null ? "" : translate("salesforceDashboard.asOf", { date: syncedAt });
  const notices = dashboard?.lastSyncedAt ? qualityNotices(dashboard) : [];
  const navigate = useNavigate();
  const openRecords = useCallback(
    (drillDown: SalesforceDrillDown) =>
      navigate({ pathname: SALESFORCE_RECORDS_ROUTE, search: drillDownSearch(drillDown) }),
    [navigate]
  );

  return (
    <Box sx={{ p: `${space.xl}px` }}>
      <Title title={translate("salesforceDashboard.title")} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: `${space.xl}px`,
          mb: `${space.lg}px`,
          p: `${space.lg}px ${space.xl}px`,
          backgroundColor: colors.headerBackground,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: "4px",
          color: colors.text,
        }}
      >
        <Box sx={{ display: "flex", gap: `${space.lg}px`, minWidth: 0 }}>
          <Box
            aria-hidden
            sx={{
              display: "flex",
              flex: "none",
              alignItems: "center",
              justifyContent: "center",
              width: `${ICON_TILE_SIZE_PX}px`,
              height: `${ICON_TILE_SIZE_PX}px`,
              backgroundColor: color.primary,
              borderRadius: "4px",
              color: color.white,
            }}
          >
            <SpeedOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.xs}px`, minWidth: 0 }}>
            <Typography sx={{ fontSize: "12px", lineHeight: 1.25, color: colors.text }}>
              {translate("salesforceDashboard.overline")}
            </Typography>
            <Typography
              component="h1"
              sx={{ m: 0, fontSize: "18px", fontWeight: 700, lineHeight: 1.25, color: colors.text }}
            >
              {translate("salesforceDashboard.title")}
            </Typography>
            {notices.map(notice => (
              <HeaderNotice key={`${notice.key}-${notice.recordType ?? ""}`}>
                {translate(`salesforceDashboard.${notice.key}`, {
                  count: notice.count,
                  recordType: notice.recordType,
                })}
              </HeaderNotice>
            ))}
            {dashboard?.lastSyncedAt && (
              <Typography sx={{ fontSize: "12px", color: colors.text }}>
                {`${asOf} · ${translate("salesforceDashboard.contracts")}: ${formatCount(
                  dashboard.contractCount
                )} · ${translate("salesforceDashboard.subscriptions")}: ${formatCount(dashboard.subscriptionCount)}`}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          disabled={loading}
          onClick={() => void refetch().catch(() => undefined)}
          startIcon={<RefreshOutlinedIcon />}
          sx={{
            height: "32px",
            backgroundColor: colors.cardBackground,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: "4px",
            fontSize: "13px",
            textTransform: "none",
            color: colors.link,
            "&:hover": { backgroundColor: colors.headerBackground, border: `1px solid ${colors.cardBorder}` },
          }}
          variant="outlined"
        >
          {translate("salesforceDashboard.refresh")}
        </Button>
      </Box>

      {loading && !dashboard && <CircularProgress aria-label={translate("salesforceDashboard.loading")} />}
      {error && (
        <Alert severity="error" sx={{ mb: `${space.lg}px` }}>
          {translate("salesforceDashboard.loadError")}
        </Alert>
      )}

      {dashboard?.lastSyncedAt === null && (
        <Paper variant="outlined" sx={{ p: `${space.xl}px` }}>
          <Typography component="h2" variant="h6">
            {translate("salesforceDashboard.noImportTitle")}
          </Typography>
          <Typography color="text.secondary">{translate("salesforceDashboard.noImportDescription")}</Typography>
        </Paper>
      )}

      {dashboard?.lastSyncedAt && <DashboardGrid asOf={asOf} dashboard={dashboard} onDrillDown={openRecords} />}
    </Box>
  );
};

export const SalesforceDashboardPage = () => (
  <RequirePermission permission={ResourceName.SalesforceDashboard}>
    <SalesforceDashboardContent />
  </RequirePermission>
);
